import React, { useCallback, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
	Control,
	Controller,
	ControllerFieldState,
	ControllerProps,
	ControllerRenderProps,
	FieldError,
	FieldPath,
	FieldValues,
	UseFormStateReturn,
} from "react-hook-form";
import KogInputText from "../inputText/inputText";
import { getDefaultMessageResultsFound, getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { uppercaseFirstLetter } from "@/main/services/utilsString";
import { debounce, isEmpty, isNil } from "lodash";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import ListBoxComponentAutoCompletePagination, {
	ID_LOADING_LIST_BOX_PAGINATION,
	ID_RESULTS_FOUND_LIST_BOX_PAGINATION,
} from "./ListBoxComponentAutoCompletePagination";
import * as S from "./styles";
import { SearchTermRequest } from "@/main/models/common/searchTermRequest";
import { Paper, PaperProps } from "@mui/material";

type IControllerProps = Omit<ControllerProps, "render" | "name">;

interface IKOGAutoCompletePaginationControlledProps<T extends FieldValues> {
	// Required Props
	idScreen: string;
	name: FieldPath<T>;
	control: Control<T>;
	loadingMessage: string;
	getData: (request: SearchTermRequest) => Promise<any>;

	// Optionals Props
	typeInput?: "outlined" | "filled";
	label?: string;
	controllerProps?: IControllerProps;
	required?: boolean;
	isPluralLabel?: boolean;
	disabledMessage?: boolean;
	disabled?: boolean;
	className?: string;
	placeholder?: string;
	onKeyDown?: (e: React.KeyboardEvent) => void;
	onChangeCustom?: (optionSelected: any | null) => void;
	autoFocus?: boolean;
}

const CustomPaper = (props: PaperProps) => {
	return <Paper elevation={8} {...props} className={`${props.className} ignore-click`} />;
};

const KOGAutoCompletePaginationControlled = <T extends FieldValues>({
	idScreen,
	control,
	name,
	label,
	loadingMessage,
	getData,
	typeInput = "filled",
	controllerProps,
	required = false,
	isPluralLabel = false,
	disabledMessage = false,
	disabled = false,
	className = "",
	placeholder,
	onKeyDown,
	onChangeCustom,
	autoFocus = false,
}: IKOGAutoCompletePaginationControlledProps<T>) => {
	const classes = S.useStyles();
	const { options, setOptions, setResult } = useKOGAutoCompletePaginationControlled();

	const renderErrorMessages = (error: FieldError | undefined) => {
		const requiredMessage = getTranslateByKey(
			!isNullUndefined(isPluralLabel) && isPluralLabel ? "information.areRequired" : "information.isRequired",
			true,
		);
		const fieldMessage = getTranslateByKey("information.field");

		if (!error) return null;
		switch (error.type) {
			case "required":
				return uppercaseFirstLetter(requiredMessage.replace("{0}", `${label || fieldMessage}`));
			default:
				return error.message;
		}
	};

	const customControllerProps: IControllerProps = {
		...controllerProps,
		...{
			rules: {
				...controllerProps?.rules,
				required: required || controllerProps?.rules?.required,
			},
		},
	};

	const useRenderInput = ({
		field,
		fieldState: { error },
	}: {
		field: ControllerRenderProps<FieldValues, string>;
		fieldState: ControllerFieldState;
		formState: UseFormStateReturn<FieldValues>;
	}) => {
		const LENGTH_CALL_API = 3;
		const DEBOUNCE_TIME = 600;
		// #region States
		const [open, setOpen] = useState(false);
		const [inputTextApi, setInputTextApi] = useState<string>("");
		const [isLoading, setIsLoading] = useState<boolean>(false);
		const [hasShowMore, setHasShowMore] = useState<boolean>(false);
		const [pageIndex, setPageIndex] = useState<number>(PaginationEnum.PageIndexIntial);
		// #endregion

		const idScreenInput = `${idScreen}-dropdown-paginated-${field.name}`;
		const autoCompleteValue = isNil(field.value) ? null : field.value;
		const baseInputProps = {
			...field,
			id: idScreenInput,
		};

		function setResultsSpan(dataLength: number, totalItems: number) {
			const element = document.getElementById(ID_RESULTS_FOUND_LIST_BOX_PAGINATION);
			if (element) {
				element.innerText = getDefaultMessageResultsFound(dataLength, totalItems);
			}
		}

		async function fetchData(searchTerm: string, pageIndex: number, clearPrevious: boolean = false) {
			setIsLoading(true);

			const request: SearchTermRequest = {
				searchTerm,
				pageIndex,
				pageSize: PaginationEnum.PageSize10,
			};

			const response = await getData(request);
			const results = response.data.results || [];
			let totalRecords = 0;
			if (clearPrevious) {
				totalRecords = results.length;
				setOptions([...results]);
			} else {
				totalRecords = options.length + results.length;
				setOptions((prev) => [...prev, ...results]);
			}
			setIsLoading(false);
			setResult(response.data);
			setHasShowMore(totalRecords < response.data.totalNumberOfRecords);
			setResultsSpan(totalRecords, response.data.totalNumberOfRecords);
		}

		// #region Functions - Reset
		function resetAllState() {
			setOptions([]);
			setInputTextApi("");
			setPageIndex(PaginationEnum.PageIndexIntial);
			setHasShowMore(false);
			field.onChange(null);
			onChangeCustom?.(null);
		}

		function clearSearchTerms() {
			setInputTextApi("");
			setPageIndex(PaginationEnum.PageIndexIntial);
			setHasShowMore(false);
		}

		function resetStatesScrollInfinity() {
			setPageIndex(PaginationEnum.PageIndexIntial);
			setHasShowMore(false);
		}
		// #endregion

		useEffect(() => {
			if (inputTextApi.length >= LENGTH_CALL_API) {
				setPageIndex(PaginationEnum.PageIndexIntial);
				fetchData(inputTextApi, PaginationEnum.PageIndexIntial, true);
			}
		}, [inputTextApi]);

		useEffect(() => {
			const elementLoading = document.getElementById(ID_LOADING_LIST_BOX_PAGINATION);
			if (elementLoading) {
				if (isLoading) {
					elementLoading.innerText = loadingMessage;
				} else {
					elementLoading.innerText = "";
				}
			}
		}, [isLoading]);

		const debounceOnChange = useCallback(debounce(setInputTextApi, DEBOUNCE_TIME), []);

		const renderOtherProps = (option: any) => {
			let result = "";

			if (!isNil(option?.username) && !isEmpty(option?.username)) {
				result += ` - ${option.username}`;
			}

			if (!isNil(option?.companyTypeName) && !isEmpty(option?.companyTypeName)) {
				result += ` (${option.companyTypeName})`;
			}

			return result;
		};

		const getOptionText = (option: any): string => {
			if (typeof option === "string") return option;

			const label =
				option?.value ?? option?.label ?? option?.name ?? option?.description ?? option?.title ?? option?.text;

			if (!isNil(label)) return String(label);
			if (!isNil(option?.key)) return String(option.key);

			return "";
		};

		return (
			<Autocomplete
				{...baseInputProps}
				freeSolo
				open={open}
				onOpen={() => {
					setOpen(true);
				}}
				onClose={() => {
					setOpen(false);
				}}
				// getOptionSelected={(option, value) => option.name === value.name}
				className={`${classes.inputAutoComplete} ignore-click ${className}`}
				value={autoCompleteValue}
				getOptionLabel={(option) => getOptionText(option)}
				renderOption={(props, option) => <li {...props}>{`${getOptionText(option)}${renderOtherProps(option)}`}</li>}
				noOptionsText={getTranslateByKey("information.noRecordsFound")}
				options={options}
				loading={isLoading}
				disabled={disabled}
				onChange={(_: React.SyntheticEvent, optionSelected: any | null) => {
					field.onChange(optionSelected ?? null);
					onChangeCustom?.(optionSelected ?? null);
					if (optionSelected) {
						setInputTextApi("");
					}
				}}
				onInputChange={(_, newInputValue, reason) => {
					if (reason === "clear") {
						resetAllState();
					} else if (reason === "reset") {
						clearSearchTerms();
					} else if (reason === "input") {
						debounceOnChange(newInputValue);
					}

					if (newInputValue === "") {
						resetStatesScrollInfinity();
						resetAllState();
					}
				}}
				PaperComponent={CustomPaper}
				renderInput={(params) => (
					<KogInputText
						label={label}
						title={getOptionText(field?.value)}
						variant={typeInput ?? "filled"}
						error={!!error}
						helperText={!disabledMessage && renderErrorMessages(error)}
						required={Boolean(customControllerProps.rules?.required)}
						placeholder={placeholder}
						onKeyDown={onKeyDown}
						autoFocus={autoFocus}
						{...params}
					/>
				)}
				ListboxProps={{
					onScroll: (event: React.UIEvent<HTMLUListElement>) => {
						const listbox = event.currentTarget;
						if (listbox.scrollHeight - (listbox.scrollTop + listbox.clientHeight) <= 5 && hasShowMore) {
							fetchData(inputTextApi, pageIndex + 1);
							setPageIndex(pageIndex + 1);
						}
					},
				}}
				ListboxComponent={ListBoxComponentAutoCompletePagination}
			/>
		);
	};

	return (
		<S.ContinerController>
			<Controller control={control as Control} name={name} {...customControllerProps} render={useRenderInput} />
		</S.ContinerController>
	);
};

interface IKOGAutoCompletePaginationControlledContextProps {
	result: any;
	options: any[];
	setOptions: React.Dispatch<React.SetStateAction<any[]>>;
	setResult: React.Dispatch<React.SetStateAction<any>>;
}
const KOGAutoCompletePaginationControlledContext =
	React.createContext<IKOGAutoCompletePaginationControlledContextProps>({
		result: null,
		options: [],
		setOptions: () => {},
		setResult: () => {},
	});
export const useKOGAutoCompletePaginationControlled = () => {
	const context = React.useContext(KOGAutoCompletePaginationControlledContext);
	if (!context) {
		throw new Error(
			"useKOGAutoCompletePaginationControlled must be used within a KOGAutoCompletePaginationControlledProvider",
		);
	}
	return context;
};
export const KOGAutoCompletePaginationControlledProvider = ({ children }: { children: React.ReactNode }) => {
	const [result, setResult] = React.useState<any>(null);
	const [options, setOptions] = useState<any[]>([]);

	return (
		<KOGAutoCompletePaginationControlledContext.Provider
			value={{
				result,
				options,
				setOptions,
				setResult,
			}}
		>
			{children}
		</KOGAutoCompletePaginationControlledContext.Provider>
	);
};

function KOGAutoCompletePaginationControlledWrapper<T extends FieldValues>(
	props: IKOGAutoCompletePaginationControlledProps<T>,
) {
	return (
		<KOGAutoCompletePaginationControlledProvider>
			<KOGAutoCompletePaginationControlled {...props} />
		</KOGAutoCompletePaginationControlledProvider>
	);
}

export default KOGAutoCompletePaginationControlledWrapper;
